import React, { useState } from 'react';
import { Box, Text, HStack, VStack, Icon } from '@chakra-ui/react';
import { FiPlay, FiCamera, FiChevronRight, FiChevronDown, FiUploadCloud, FiShare2 } from 'react-icons/fi';
import { Campaign, Collab, ContentRequirement } from '@foodfluence/shared';
import {
  formatUgcPostingNote,
  getShortDurationText,
  lowerFirst,
  pluralize,
  summarizeUgcDeliverables,
  UgcDeliverableItem,
  UgcDeliverablesSummary,
} from '../../../../utils/contentUtils';
import ViewGuidelinesSheet from './ViewGuidelinesSheet';
import { ff } from '../../../../theme';
import { Pill } from '../../../../components/ui';

/** Lists of this many items or more collapse to COLLAPSED_ROWS rows plus a "+N more" row. */
const COLLAPSE_AT = 6;
const COLLAPSED_ROWS = 4;

const summaryCopy = ({ items, videos, photos, sharedVideoDuration }: UgcDeliverablesSummary) => {
  if (videos && photos) {
    return {
      title: 'Unique UGC deliverables',
      subtitle: `${pluralize(videos, 'video')} and ${pluralize(photos, 'photo')}, each one different. Check each one's guidelines.`,
    };
  }
  if (photos) {
    return items.length === 1
      ? { title: 'UGC photo', subtitle: 'Check the guidelines.' }
      : { title: 'Unique UGC photos', subtitle: "Each one different. Check every photo's guidelines." };
  }
  if (items.length === 1) {
    return { title: 'UGC video', subtitle: sharedVideoDuration ? `${sharedVideoDuration}. Check the guidelines.` : 'Check the guidelines.' };
  }
  return {
    title: 'Unique UGC videos',
    subtitle: sharedVideoDuration
      ? `Each one different, ${lowerFirst(sharedVideoDuration)}. Check every video's guidelines.`
      : "Each one different; lengths vary. Check every video's guidelines.",
  };
};

const itemLabel = ({ type, index, ofType }: UgcDeliverableItem) => `${type === 'video' ? 'Video' : 'Photo'} ${index} of ${ofType}`;

const ItemTile: React.FC<{ item: UgcDeliverableItem; small?: boolean }> = ({ item, small }) => (
  <VStack
    w={small ? '28px' : '40px'}
    h={small ? '28px' : '40px'}
    flexShrink={0}
    spacing={0}
    justify="center"
    borderRadius={small ? 'full' : '12px'}
    bg={ff.grey}
    border={small ? '2px solid white' : undefined}
    color={ff.ink}
  >
    {!small && <Icon as={item.type === 'video' ? FiPlay : FiCamera} boxSize="14px" />}
    <Text fontSize="11px" fontWeight={600} lineHeight={1}>
      {item.index}
    </Text>
  </VStack>
);

const rowStyle = {
  as: 'button' as const,
  w: '100%',
  minH: '64px',
  textAlign: 'left' as const,
  spacing: 3,
  py: 3,
  bg: 'white',
  _hover: { bg: ff.grey },
  transition: 'background 0.15s',
};

interface UgcDeliverablesSectionProps {
  campaign: Campaign;
  collab?: Collab;
  clientName?: string;
  /** Optional — called in addition to opening the built-in sheet */
  onViewGuidelines?: (requirement: ContentRequirement) => void;
}

const UgcDeliverablesSection: React.FC<UgcDeliverablesSectionProps> = ({
  campaign,
  collab,
  clientName,
  onViewGuidelines,
}) => {
  const [sheetRequirement, setSheetRequirement] = useState<ContentRequirement | null>(null);
  const [expanded, setExpanded] = useState(false);

  const summary = summarizeUgcDeliverables(collab, campaign);
  const { items } = summary;
  if (items.length === 0) return null;

  const collapsed = items.length >= COLLAPSE_AT && !expanded;
  const shownItems = collapsed ? items.slice(0, COLLAPSED_ROWS) : items;
  const hiddenItems = collapsed ? items.slice(COLLAPSED_ROWS) : [];
  const hiddenNoun = hiddenItems.every(i => i.type === 'video')
    ? 'videos'
    : hiddenItems.every(i => i.type === 'photo')
    ? 'photos'
    : 'deliverables';
  const { title, subtitle } = summaryCopy(summary);
  const postingNote = formatUgcPostingNote(summary);

  const handleViewGuidelines = (requirement: ContentRequirement) => {
    setSheetRequirement(requirement);
    onViewGuidelines?.(requirement);
  };

  return (
    <>
      <VStack align="stretch" spacing={3} fontFamily="display">
        {/* Section header */}
        <Text as="h2" m={0} textStyle="eyebrow" fontSize="12px" color={ff.text2}>
          Deliverables
        </Text>

        {/* Summary: the total owed, so a multi-video ask can't read as one video */}
        <VStack align="stretch" spacing={3} p="18px" borderRadius="20px" bg={ff.limeSurface} color={ff.ink}>
          <HStack spacing={4} align="center">
            <Text textStyle="display" fontSize="36px" lineHeight={1} flexShrink={0}>
              {items.length}
            </Text>
            <VStack align="start" spacing="4px">
              <Text textStyle="eyebrow" fontSize="11px" color={ff.text2}>
                Summary
              </Text>
              <Text fontSize="17px" fontWeight={600} lineHeight={1.25}>
                {title}
              </Text>
              <Text fontSize="14px" color={ff.body} lineHeight={1.4}>
                {subtitle}
              </Text>
            </VStack>
          </HStack>
          {postingNote && (
            <HStack spacing={2} pt={3} borderTop="1px solid" borderColor="#E0E0DD">
              <Icon as={items.some(i => i.posted) ? FiShare2 : FiUploadCloud} boxSize={4} flexShrink={0} />
              <Text fontSize="14px" fontWeight={600}>
                {postingNote}
              </Text>
            </HStack>
          )}
        </VStack>

        {/* One row per piece of content */}
        <Box border="1px solid" borderColor={ff.card} borderRadius="20px" px={4} overflow="hidden">
          <VStack align="stretch" spacing={0} divider={<Box h="1px" bg={ff.line} border="none" />}>
            {shownItems.map(item => {
              const duration = item.type === 'video' ? getShortDurationText(item.requirement.duration) : '';
              return (
                <HStack
                  key={`${item.type}-${item.index}`}
                  {...rowStyle}
                  aria-label={`View guidelines for ${itemLabel(item)}`}
                  onClick={() => handleViewGuidelines(item.requirement)}
                >
                  <ItemTile item={item} />
                  <VStack align="start" spacing="2px" flex={1} minW={0}>
                    <Text fontSize="15px" fontWeight={600} color={ff.ink}>
                      {itemLabel(item)}
                    </Text>
                    <Text fontSize="13px" color={ff.text2} noOfLines={1}>
                      {duration ? `${duration} · View guidelines` : 'View guidelines'}
                    </Text>
                  </VStack>
                  <Pill tone="ink">UGC</Pill>
                  <Icon as={FiChevronRight} boxSize={4} color={ff.muted} flexShrink={0} />
                </HStack>
              );
            })}

            {collapsed && (
              <HStack {...rowStyle} aria-expanded={false} onClick={() => setExpanded(true)}>
                <HStack spacing={-2} flexShrink={0} pl={0.5}>
                  {hiddenItems.slice(0, 3).map(item => (
                    <ItemTile key={`${item.type}-${item.index}`} item={item} small />
                  ))}
                </HStack>
                <VStack align="start" spacing="2px" flex={1} minW={0} pl={1}>
                  <Text fontSize="15px" fontWeight={600} color={ff.ink}>
                    {`+${hiddenItems.length} more ${hiddenNoun}`}
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    {`Tap to show all ${items.length}`}
                  </Text>
                </VStack>
                <Icon as={FiChevronDown} boxSize={5} color={ff.muted} flexShrink={0} />
              </HStack>
            )}
          </VStack>
        </Box>
      </VStack>

      <ViewGuidelinesSheet
        isOpen={sheetRequirement !== null}
        onClose={() => setSheetRequirement(null)}
        requirement={sheetRequirement}
        clientId={campaign.clientID}
        clientName={clientName}
      />
    </>
  );
};

export default UgcDeliverablesSection;
